import { Group, Image, Modal, Stack, Text } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { notifications } from "@mantine/notifications"
import { Button, Card, EqualWidthGrid, Loader, SectionHeader } from "@tfg/ui"
import i18next from "i18next"
import _ from "lodash"
import { useTranslation } from "react-i18next"
import { useNavigate, useOutletContext } from "react-router-dom"

import { LibraryResponses } from "@tfg/types/endpoints/library"

import { Property } from "~/components/Property.tsx"
import { DateCell } from "~/components/cells/DateCell.tsx"
import { UnitCell } from "~/components/cells/UnitCell.tsx"
import { useFindOperationChainsQuery } from "~/pages/Admin/Ingestion/OpChains/hooks/useFindOperationChainsQuery"
import { useGetOrganizationSettingsQuery } from "~/pages/Brand/OrganizationSettings/ProductPages/hooks.ts"
import { AGECDetails } from "~/pages/Brand/Products/Details/AGECDetails.tsx"
import { ComponentDetails } from "~/pages/Brand/Products/Details/ComponentDetails.tsx"
import { ProductCustomFieldGraph } from "~/pages/Brand/Products/Details/CustomFieldGraph/ProductCustomFieldGraph"
import { SuppliersList } from "~/pages/Brand/Products/Details/SuppliersList.tsx"
import { ProductTraceability } from "~/pages/Brand/Products/Details/TraceabilityGraph/ProductTraceability.tsx"
import { useDeleteProductMutation } from "~/pages/Brand/Products/Details/hooks.ts"
import { useAuth } from "~/store/auth-context.tsx"

export const ProductDetails = () => {
  const { t } = useTranslation("products")
  const navigate = useNavigate()
  const { user } = useAuth()

  const [opened, { open, close }] = useDisclosure(false)
  const product = useOutletContext<LibraryResponses.ProductDetails>()
  const { data: operationChains, isLoading } = useFindOperationChainsQuery({})
  const { mutate } = useDeleteProductMutation(product.id)
  const { data: org } = useGetOrganizationSettingsQuery(user?.organization?.id)
  const customFieldSectionEnabled = org?.settings.customFieldsPlan === "EXPERT"
  const deleteAction = () => {
    mutate()
    notifications.show({
      title: t("details.delete.success"),
      message: "",
    })
    close()
    navigate("/products")
  }

  if (isLoading) {
    return <Loader fullPage />
  }

  const productOperationChains = operationChains?.data?.filter(
    (opChain) => opChain.type === "PRODUCT"
  )
  const componentOperationChains = operationChains?.data?.filter(
    (opChain) => opChain.type === "COMPONENT"
  )
  const materialOperationChains = operationChains?.data?.filter(
    (opChain) => opChain.type === "MATERIAL"
  )

  return (
    <>
      <Modal
        opened={opened}
        onClose={close}
        title={t("details.delete.title")}
        centered
      >
        <Stack>
          <Text size="sm" mb="xs" fw={400}>
            {t("details.delete.message")}
          </Text>
          <EqualWidthGrid>
            <Button
              fullWidth={true}
              onClick={close}
              variant="secondary-critical"
            >
              {t("details.delete.cancel")}
            </Button>
            <Button
              fullWidth={true}
              onClick={() => deleteAction()}
              variant="primary-critical"
            >
              {t("details.delete.submit")}
            </Button>
          </EqualWidthGrid>
        </Stack>
      </Modal>
      <Stack>
        <SectionHeader title={t("details.sections.informations")} divider />
        <Card w="fit-content">
          <Stack justify="flex-start">
            <Group gap="xl">
              <Image w={250} h={250} src={product.picture} alt={product.name} />
              <Stack>
                <Property
                  label={t("details.properties.garment")}
                  value={product.type.typeTextContent.originalText}
                />
                <Property
                  label={t("details.properties.mass")}
                  value={
                    <UnitCell value={product.weight ?? undefined} unit="g" />
                  }
                />
                <Property
                  label={t("details.properties.reference")}
                  value={product.item.reference ?? null}
                />
                <Property
                  label={t("details.properties.purchaseOrder")}
                  value={product.purchaseOrder?.reference}
                />
                <Property
                  label={t("details.properties.color")}
                  value={product.color}
                />
                <Property
                  label={t("details.properties.collection")}
                  value={product.collection}
                />
                <Property
                  label={t("details.properties.marketLaunchDate")}
                  value={
                    <DateCell date={product.marketLaunchDate ?? undefined} />
                  }
                />
              </Stack>
            </Group>
            <Button variant="primary-critical" onClick={open}>
              {t("details.actions.delete")}
            </Button>
          </Stack>
        </Card>
        <SectionHeader title={t("details.sections.agec")} divider />
        <AGECDetails product={product} />

        <SectionHeader title={t("details.sections.composition")} divider />
        <Group align="flex-start" gap="xl">
          {_.sortBy(
            product.components,
            (component) => -component.mainFabric
          ).map((component) => (
            <ComponentDetails
              key={component.id}
              component={component}
              product={product}
            />
          ))}
        </Group>

        <SectionHeader title={t("details.sections.suppliers")} divider />
        <SuppliersList product={product} />

        <SectionHeader title={t("details.sections.traceability")} divider />
        <ProductTraceability
          product={product}
          operationsChains={{
            productOperationChains: productOperationChains ?? [],
            componentOperationChains: componentOperationChains ?? [],
            materialOperationChains: materialOperationChains ?? [],
          }}
        />
        {customFieldSectionEnabled && (
          <>
            <SectionHeader title={t("details.sections.customFields")} divider />
            <ProductCustomFieldGraph product={product} />
          </>
        )}
      </Stack>
    </>
  )
}

type CustomFieldsProps = {
  product: LibraryResponses.ProductDetails
}

export const ProductCustomFields = ({ product }: CustomFieldsProps) => {
  const { data: org } = useGetOrganizationSettingsQuery()
  const customFieldsDefs = org?.enabledCustomFields || []
  const allCfv = product.operationTree.nodes.flatMap((n) => {
    const cfv =
      n.operation != "not-entered-yet" ? n.operation.customFieldValues : []
    return cfv
  })
  return (
    <>
      {customFieldsDefs.map((cfd) => {
        const cfv = allCfv.find((cfv) => cfd.id === cfv.customFieldDefinitionId)
        let label = cfd.textContent.TextTranslation.find(
          (l) => l.translation === i18next.language.toUpperCase()
        )
        if (!label) {
          label = cfd.textContent.TextTranslation.find(
            (l) => l.translation === "EN"
          )
        }
        return (
          <Property
            key={cfd.id}
            label={label?.translation ?? ""}
            value={cfv?.value}
          />
        )
      })}
    </>
  )
}
